import { inject, Injectable } from "@angular/core";
import { MetadataTableService } from "./metadataTableService";
import { Utilities } from "@tylertech/shared-module";
import { MetadataService } from "../metadata.service";
import { IGridViewMetadata, SelectionMode } from "@tylertech/tyler-api-types";
import { IgxGridComponent, IRowSelectionEventArgs } from "@infragistics/igniteui-angular";
import { TableRowSelectArgs } from "../../interfaces";
import { ProviderStorageService } from "./provider.storage.service";
import { NavigationService } from "@tylertech/core-module";
import { ResetStateService } from "../reset.state.service";

@Injectable({ providedIn: "root" })
export class GridSelectionService extends ResetStateService
{
    SelectedIds: Record<string, string[]> = {};
    SelectedCount: Record<string, number> = {};
    AllSelected: boolean = false;
    AllDeselected: boolean = false;
    RowSelectRecalculate: boolean = false;
    Selecting: boolean = false;
    RowSelect: boolean = false;
    protected rowSelecting: boolean = false;
    protected disabledSelection: Record<string, string[]> = {};

    constructor(
        private readonly $metadataService: MetadataService,
        private readonly $metadataTableService: MetadataTableService,
        private readonly $providerStorageService: ProviderStorageService
    )
    {
        super(inject(NavigationService));
    }

    SetSelected(
        providerId: string,
        grid: IgxGridComponent,
        forceInitial: boolean,
        viewMetadata: IGridViewMetadata,
        allSelected: boolean = false,
        allDeselected: boolean = false,
        forceDeselect: boolean = false,
        fromRecalculate: boolean = false
    ): void
    {
        const self = this;
        setTimeout(() =>
        {
            if (forceDeselect)
            {
                this.$providerStorageService.GridData[providerId]?.forEach(x => (x["Selected"] = false));
                this.$providerStorageService.LocalEntitiesStore[providerId].forEach(x =>
                {
                    x[viewMetadata.SelectProperty ?? "Selected"] = false;
                });
            }
            if (this.$providerStorageService.LocalEntitiesStore[providerId] && (forceInitial || fromRecalculate))
                self.setInitialSelectedLocalPaged(providerId, allSelected, allDeselected, viewMetadata, fromRecalculate);
            if (allDeselected || !this.HasSelection(providerId))
                grid.selectRows([], true);
            else if (this.HasSelection(providerId))
                grid.selectRows(self.currentPageSelection(providerId, grid), true);
        }, 5);
    }

    OnRowSelect(
        providerId: string, event: IRowSelectionEventArgs, viewMetadata: IGridViewMetadata,
        grid: IgxGridComponent, doOnSelected: (args: TableRowSelectArgs) => void
    ): void
    {
        const self = this;
        if (this.rowSelecting)
            return cancel();
        this.rowSelecting = true;
        let args: TableRowSelectArgs;
        if (!this.RowSelect)
        {
            const addedAllowed = event.added.filter(x => this.IsSelectionAllowed(providerId, x));
            const selectAll = addedAllowed.length > 0;
            this.headerSelect(event, selectAll, providerId, viewMetadata, grid);
            args = {
                selectAll: selectAll,
                deselectAll: !selectAll,
                added: [],
                removed: []
            };
        }
        else
        {
            this.RowSelect = false;
            const removed = event.removed.filter(x => this.IsSelectionAllowed(providerId, x) && !event.added.includes(x));
            const added = event.added.filter(x => this.IsSelectionAllowed(providerId, x) && !event.removed.includes(x));

            const oldSelection: string[] = this.SelectedIds[providerId];
            if (viewMetadata.SelectionMode === SelectionMode.Single && removed.length === 0 && oldSelection?.length > 0)
            {
                removed.push(oldSelection[0]);
            }
            event.newSelection = event.newSelection.filter(x => this.IsSelectionAllowed(providerId, x) || (!this.IsSelectionAllowed(providerId, x) && oldSelection && oldSelection.includes(x)));
            if (added.length === 0 && removed.length === 0)
                return cancel();
            if (event.newSelection.length > 0)
            {
                self.AllDeselected = false;
            }
            self.Selecting = true;
            removed.forEach(x => applySelection(x, false));
            added.forEach(x => applySelection(x, true));
            this.SetSelectedIds(providerId, event.newSelection);
            if (viewMetadata["$LookupDetail"])
                viewMetadata["$LookupDetailId"] = added[0];
            this.RowSelectRecalculate = true;
            args = {
                selectAll: false,
                deselectAll: false,
                added: added,
                removed: removed
            };
        }
        doOnSelected(args);
        setTimeout(() => self.rowSelecting = false, 200);
        function applySelection(id: any, selected: boolean): void
        {
            const row = self.$providerStorageService.GridData[providerId].find(i => i.Id === id);
            if (row) row.Selected = selected;
        }
        function cancel(): void
        {
            self.RowSelect = false;
            self.rowSelecting = false;
            event.cancel = true;
        }
    }

    IsSelectionAllowed(providerId: string, id: string): boolean
    {
        if (!this.disabledSelection[providerId]) this.disabledSelection[providerId] = [];
        return this.disabledSelection[providerId].indexOf(id) === -1;
    }

    SetSelectDisabled(providerId: string, viewMetadata: IGridViewMetadata): void
    {
        if (
            !viewMetadata ||
            Utilities.IsNullOrUndefinedOrEmpty(viewMetadata?.GridProperties.AllowSelectProperty)
        )
            return;
        let models = [];
        if (this.$providerStorageService.LocalEntitiesStore[providerId])
            models = this.$providerStorageService.LocalEntitiesStore[providerId];
        else models = this.$providerStorageService.GridData[providerId].map(x => x["$$model"]);
        this.disabledSelection[providerId] = [];
        models.forEach(x =>
        {
            if (
                !this.$metadataService.EvaluateBoolean(
                    viewMetadata.GridProperties.AllowSelectProperty,
                    x["$$Parent"],
                    x
                ) &&
                this.disabledSelection[providerId].indexOf(
                    this.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0)
                ) === -1
            )
                this.disabledSelection[providerId].push(
                    this.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0)
                );
        });
    }

    AddSelected(providerId: string, id: string): void
    {
        this.SelectedIds[providerId].push(id);
        this.SelectedCount[providerId] = this.SelectedIds[providerId].length;
        this.AllDeselected = false;
    }

    RemoveSelected(providerId: string, index: number): void
    {
        this.SelectedIds[providerId].splice(index);
        this.SelectedCount[providerId] = this.SelectedIds[providerId].length;
    }

    SetSelectedIds(providerId: string, ids: string[])
    {
        this.SelectedIds[providerId] = ids;
        this.SelectedCount[providerId] = ids.length;
    }

    HasSelection(providerId: string): boolean
    {
        return this.SelectedCount[providerId] > 0;
    }

    private setInitialSelectedLocalPaged(providerId: string, allSelected: boolean, allDeselected: boolean, viewMetadata: IGridViewMetadata, fromRecalculate: boolean): void
    {
        const self = this;
        if (allSelected)
        {
            const selected = this.$providerStorageService.LocalEntitiesStore[providerId]
                .filter(x => (x["$inFilter"] && x.SerializedRowState !== "Deleted" && this.IsSelectionAllowed(providerId, self.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0))
                    || (!this.IsSelectionAllowed(providerId, self.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0))
                        && x.Selected === true)) && checkSelectProperty(x))
                .map(x => self.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0));
            this.SetSelectedIds(providerId, selected);
        }
        else if (allDeselected)
        {
            this.SetSelectedIds(providerId, []);
        }
        else
        {
            const selected = this.$providerStorageService.LocalEntitiesStore[providerId]
                .filter(f => f["$inFilter"] && f.SerializedRowState !== "Deleted" && f[viewMetadata.SelectProperty ?? "Selected"])
                .map(x => self.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0));
            this.SetSelectedIds(providerId, selected);
        }

        function checkSelectProperty(entity: any): boolean
        {
            if (!fromRecalculate)
                return true;
            const selectProperty = viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected";
            return entity[selectProperty];
        }
    }

    private headerSelect(event: any, selectAll: boolean, providerId: string, viewMetadata: IGridViewMetadata, grid: IgxGridComponent): void
    {
        let newSelection = [];
        const selectProperty = viewMetadata?.SelectProperty ? viewMetadata.SelectProperty : "Selected";
        if (!selectAll)
        {
            if (this.$providerStorageService.LocalEntitiesStore[providerId])
            {
                newSelection = this.$providerStorageService.LocalEntitiesStore[providerId]
                    .filter(x =>
                        !this.IsSelectionAllowed(providerId, this.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0)) &&
                        this.$metadataService.EvaluateBoolean(selectProperty, x["$$Parent"], x))
                    .map(m => this.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, m, 0));
            }
            else
            {
                newSelection = this.$providerStorageService.GridData[providerId]
                    .filter(x => !this.IsSelectionAllowed(providerId, x.Id) && x.Selected === true)
                    .map(m => m.Id);
            }
            this.AllSelected = false;
            if (newSelection.length === 0)
                this.AllDeselected = true;
        }
        else if (selectAll)
        {
            if (this.$providerStorageService.LocalEntitiesStore[providerId])
            {
                newSelection = this.$providerStorageService.LocalEntitiesStore[providerId]
                    .filter(x => x["$inFilter"] && x.SerializedRowState !== "Deleted" &&
                        (this.IsSelectionAllowed(providerId, this.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, x, 0)))
                        || this.$metadataService.EvaluateBoolean(selectProperty, x["$$Parent"], x))
                    .map(m => this.$metadataTableService.GetId(viewMetadata.GridProperties?.IdProperty, m, 0));
            }
            else
            {
                newSelection = this.$providerStorageService.GridData[providerId]
                    .filter(x =>
                        this.IsSelectionAllowed(providerId, x.Id) || x.Selected === true)
                    .map(m => m.Id);
            }
            this.AllSelected = true;
            this.AllDeselected = false;
        }
        this.SetSelectedIds(providerId, newSelection);
        event.newSelection = this.currentPageSelection(providerId, grid);
        this.RowSelectRecalculate = true;
    }

    private currentPageSelection(providerId: string, grid: IgxGridComponent): string[]
    {

        const currentPageIds = grid.data.map(m => m.Id);
        return this.SelectedIds[providerId].filter(id => currentPageIds.indexOf(id) > -1);
    }
}
